import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { toJS } from 'mobx';

import {
    BigNumberBox,
    PrimaryButton,
    ProductCardLayout,
    PageBehaviourButton,
    BigNumberFormatValues,
    getDesignationMessage,
} from '@ratehub/base-ui';
import { trackHeapEventForDepositsAndNavigate, DEPOSITS_VERTICALS } from '@ratehub/inv-common';

import GICsAccountPropType from '../definitions/GICsAccountPropType';
import GICRatesFooter from '../components/GICRatesFooter';
import DepositsProductName from '../components/DepositsProductName';
import ProductListGICsDetails from '../components/ProductListGICsDetails';
import ExpandableUnorderedList from '../components/ExpandableUnorderedList';


function ProductCardLayoutGICs({
    gic,
    balance,
    provinceCode,
    ...otherProps
}) {
    const productCard = getProductCardLayoutPropsForGICs(gic, balance, provinceCode);

    return (
        <ProductCardLayout
            image={productCard.image}
            name={productCard.name}
            topRightElement={(
                <BigNumberBox
                    {...productCard.bigNumber}
                />
            )}

            details={productCard.details}
            rightOfDetailsElement={productCard.callToAction}

            footer={productCard.footer}
            designationMessage={productCard.designationMessage}
            messageBanner={productCard.messageBanner}
            analyticsObject={productCard.analyticsObject}
            {...otherProps}
        >
            {productCard.children}
        </ProductCardLayout>
    );
}

ProductCardLayoutGICs.propTypes = {
    gic: GICsAccountPropType,
    balance: PropTypes.number.isRequired,
    provinceCode: PropTypes.string,
};


function getProductCardLayoutPropsForGICs(product, balance, provinceCode) {
    const {
        id,
        isSponsored,
        name,
        provider,
        isPFAWinner,
        isMonetized,
        interest,
        totalReturn,
        term,
        type,
        minimumInvestment,
        interestCompoundTerm,
        interestPaidTerm,
        isCDIC,
        redeemable,
        eligibility,
    } = product;


    return {
        designationMessage: getDesignationMessage({ isSponsored, isMonetized }),

        name: {
            Component: (
                <DepositsProductName
                    name={name}
                    provider={provider}
                    isPFAWinner={isPFAWinner}
                    onClick={
                        product.isMonetized
                            ? () => trackHeapEventForDepositsAndNavigate({
                                product,
                                provinceCode,
                                shouldNavigate: true,
                                vertical: DEPOSITS_VERTICALS.GIC,
                            })
                            : undefined
                    }
                />
            ),
        },
        bigNumber: getBigNumberMessages({
            balance,
            interest,
            totalReturn,
        }),

        details: (
            <ProductListGICsDetails
                term={term}
                type={type}
                minimumInvestment={minimumInvestment}
            />
        ),

        // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
        callToAction: isMonetized
            ? (
                <PrimaryButton
                    size="small"
                    message={(
                        <FormattedMessage
                            id="web-components.gics-account.cta"
                            defaultMessage="Get this rate"
                        />
                    )}
                    onClick={() => trackHeapEventForDepositsAndNavigate({
                        product,
                        provinceCode,
                        shouldNavigate: true,
                        vertical: DEPOSITS_VERTICALS.GIC,
                    })}
                    data-name="cta-click-gic"
                />
            )
            : undefined,

        children: MoreDetails(product),

        footer: (
            <GICRatesFooter
                id={id}
                interestCompoundTerm={interestCompoundTerm}
                interestPaidTerm={interestPaidTerm}
                isCDIC={isCDIC}
                isRedeemable={redeemable}
                eligibility={eligibility}
            />
        ),

        analyticsObject: product,
    };
}


function getBigNumberMessages({ interest, totalReturn, balance }) {
    return {
        message: (
            <FormattedMessage
                key="big-number-title"
                id="web-components.gics.big-number.interest"
                defaultMessage="<bigger>{interest, number, percent2}</bigger>"
                values={{
                    interest: interest / 100,
                    ...BigNumberFormatValues,
                }}
            />
        ),

        subMessage: (
            <FormattedMessage
                key="big-number-sub-message"
                id="web-components.gics.big-number.sub-message"
                defaultMessage="interest rate"
            />
        ),

        otherMessages: [
            {
                message: (
                    <FormattedMessage
                        key="big-number-total-return"
                        id="web-components.gics.big-number.first-year-return"
                        defaultMessage="<b>{totalReturn, number, CAD0}</b> total return based on"
                        values={{ totalReturn: totalReturn }}
                    />
                ),
            },

            {
                message: (
                    <FormattedMessage
                        key="big-number-balance"
                        id="web-components.gics.big-number.balance"
                        defaultMessage="<b>{balance, number, CAD0}</b> investment"
                        values={{ balance: balance }}
                    />
                ),
            },
        ],
    };
}


function MoreDetails(product) {
    const {
        interestNotes,
        isMonetized,
    } = product;

    // Must use a shallow copy because we are mutating the array
    const items = [ ...toJS(interestNotes) ];

    if (!isMonetized) {
        items.push(
            <FormattedMessage
                id="web-components.gics.read-more-provider-link"
                defaultMessage="<learnMoreElsewhere>Learn more on provider site</learnMoreElsewhere>"
                values={{
                    learnMoreElsewhere: msg => (
                        <PageBehaviourButton
                            data-name="cta-click-gic-learn-more"
                            size="extra-small"
                            shouldLowerCaseMessage={false}
                            onClick={() =>
                                trackHeapEventForDepositsAndNavigate({
                                    product,
                                    shouldNavigate: true,
                                    vertical: DEPOSITS_VERTICALS.GIC,
                                })
                            }
                            message={msg}
                        />
                    ),
                }}
            />,
        );
    }

    if (!items.length) {
        return undefined;
    }

    return (
        <ExpandableUnorderedList
            title={(
                <FormattedMessage
                    id="web-components.gics.expandable-list-title"
                    defaultMessage="Other information"
                />
            )}
            items={items}
        />
    );
}

MoreDetails.propTypes = GICsAccountPropType;


export default ProductCardLayoutGICs;
